'use client';

import type { ReactNode } from 'react';
import { cn } from '@fshr/shared-react';
import { useStickyHeaderOffset } from '@fshr/hooks';
import { useMediaQuery } from 'react-responsive';
import BevSafeText from '../bev-safe-text';
import Breadcrumb from '../breadcrumb';
import { SplitPageHeroTags } from './split-page-hero-tags';
import { SplitPageHeroHighlights } from './split-page-hero-highlights';
import { SplitPageHeroGallery } from './split-page-hero-gallery';
import type { SplitPageHeroProps } from './types';

export default function SplitPageHero({
	title,
	subhead,
	images,
	fallbackImage,
	durationTag,
	occupancyTag,
	taxonomy,
	guestIntentTag,
	description,
	highlights,
	breadcrumb,
	className,
}: SplitPageHeroProps) {
	const isMobile = useMediaQuery({ maxWidth: 700 });
	const stickyOffset = useStickyHeaderOffset();

	const tags = (
		<SplitPageHeroTags
			durationTag={durationTag}
			occupancyTag={occupancyTag}
			taxonomy={taxonomy}
			guestIntentTag={guestIntentTag}
		/>
	);

	// Title-to-subhead (heading-s) and the group-to-tags gap (stack-m mobile /
	// stack-l desktop) are two different Figma relationships that used to
	// collapse into one flat gap-3 (12px, matching neither) — verified against
	// the Split Page Hero Figma frames' bound variables (desktop 1440 / mobile
	// 375). On desktop the description is the frame's third child (`after`).
	const renderTitleGroup = (after?: ReactNode) => (
		<div className="flex flex-col gap-y-stack-m md:gap-y-stack-l">
			<div className="flex flex-col gap-y-heading-s">
				<h1 className="text-display2 text-text-heading">
					<BevSafeText>{title}</BevSafeText>
				</h1>
				{subhead && (
					<p className="text-h3 text-text-secondary">
						<BevSafeText>{subhead}</BevSafeText>
					</p>
				)}
			</div>
			{tags}
			{after}
		</div>
	);

	// The breadcrumb leads the left content column, above the title (Figma). It
	// stays out of the gallery column so that column — and its sticky pin under
	// the nav — starts at the very top of the hero. The breadcrumb-to-title gap
	// ("2 Column Hero" itemSpacing) is bound to stack-l on mobile and stack-xl
	// on desktop. The breadcrumb brings its own card-padding-s top inset, so the
	// column drops its top padding while it leads.
	const renderContentLede = (after?: ReactNode) =>
		breadcrumb?.length ? (
			<div className="flex flex-col gap-y-stack-l md:gap-y-stack-xl">
				<Breadcrumb items={breadcrumb} fullWidth />
				{renderTitleGroup(after)}
			</div>
		) : (
			renderTitleGroup(after)
		);
	// Only ever rendered in the desktop branch below (>=701px, the same
	// threshold as md:), so the bare (mobile) tier of the old pb-6/py-6 was
	// dead code — simplified to the one value that's ever live, and moved off
	// Tailwind's default scale (pb-10/py-10 = 40px there, silently off this
	// repo's token system) onto the real numeric-scale class.
	const contentColumnPad = breadcrumb?.length ? 'pb-400' : 'py-400';

	// UI AC 6b: on mobile the two-column layout collapses to a single column
	// ordered title -> subhead -> tags -> image gallery -> description -> highlights.
	if (isMobile) {
		return (
			// Every mobile child except the gallery sits at a flat 15px inset from
			// the frame edge (Figma: breadcrumb/title/bubbles all individually
			// carry that inset, not one shared wrapper padding) — the gallery
			// stays full-bleed, cancelling the section's own px-150.
			<section className={cn('flex flex-col gap-y-stack-l px-150 text-left', className)}>
				{renderContentLede()}

				{/* w-auto (not the gallery's own w-full) so the stretched flex item absorbs
				    the negative margins and actually reaches both viewport edges. */}
				<SplitPageHeroGallery
					images={images}
					fallbackImage={fallbackImage}
					className="-mx-150 w-auto"
				/>

				<div className="flex flex-col gap-y-stack-l">
					{description && (
						<p className="text-body2 text-text-primary">
							<BevSafeText>{description}</BevSafeText>
						</p>
					)}
					<SplitPageHeroHighlights highlights={highlights} />
				</div>
			</section>
		);
	}

	// UI AC 1a: two-column layout — content on the left, image gallery on the right.
	return (
		<section
			className={cn('flex min-h-[500px] flex-col text-left md:flex-row md:items-start', className)}
		>
			<div
				data-testid="split-page-hero-content-column"
				className={cn(
					'flex w-full flex-col gap-y-stack-xl',
					// At md this is still the standard symmetric wide-grid gutter (60px
					// both sides — no tablet frame in Figma, but spacing-wide-grid's own
					// tablet value is the sitewide default every other PDP section
					// already uses, not a made-up intermediate). From lg up it diverges
					// asymmetrically: left edge tracks the sitewide wide-grid's left
					// edge (the same dynamic cqw formula FullBleedItinerary's content
					// column uses for ITS tracking edge, decision 0012, mirrored to the
					// opposite side here since the gallery bleeds right instead of the
					// image bleeding left); right edge becomes a fixed 30px gap to the
					// full-bleed gallery column instead of continuing to grow. Verified
					// against the Split Page Hero desktop (1440) frame's real bounding
					// boxes: content starts 90px from the frame's left edge (the lg
					// wide-grid gutter) and ends a flat 30px before the gallery. No xl
					// frame exists, so the 30px gap holds flat rather than growing.
					'md:pl-600 md:pr-600',
					'lg:pl-[max(calc(var(--spacing-300)*3),calc((100cqw_-_var(--wide-grid-container-width-lg))/2))] lg:pr-300',
					'xl:pl-[max(calc(var(--spacing-600)*2),calc((100cqw_-_var(--wide-grid-container-width-xl))/2))] xl:pr-300',
					// Figma splits the columns at 50% +/- half the 30px content-to-gallery
					// gap from lg up (1440: 735 | 705, 1920: 975 | 945).
					'md:w-1/2 lg:w-[calc(50%+var(--spacing-150))]',
					contentColumnPad
				)}
			>
				{renderContentLede(
					description && (
						<p className="text-body2 text-text-primary">
							<BevSafeText>{description}</BevSafeText>
						</p>
					)
				)}
				<SplitPageHeroHighlights highlights={highlights} />
			</div>

			<div
				className="flex w-full md:sticky md:w-1/2 lg:w-[calc(50%-var(--spacing-150))]"
				style={{ top: stickyOffset }}
			>
				<SplitPageHeroGallery
					images={images}
					fallbackImage={fallbackImage}
					className="h-full w-full"
				/>
			</div>
		</section>
	);
}
